import React, { useEffect, useRef } from 'react'
import useCrashStore from '../store/useCrashStore'
import { PHASES } from '../config/crashConfig'
import { hundredsToX } from '../utils/crashHelpers'
import { createSampler, fitCanvas, ampOf, ECG_COLORS } from '../../../playground/crash-viz/ecgKit'
import audioService from '../../../services/audioService'

/**
 * CrashOscilloscope — фосфорный осциллограф (ЭКГ) для боевого режима краш.
 *
 * Читает множитель и фазы напрямую из useCrashStore через getState() в RAF
 * (как useCrashMultiplier — без ре-рендеров на каждый тик).
 * Звуки (mp3 из public/sounds/crash/, генерируются скриптом
 * scripts/render-crash-sounds.mjs): тик на R-пике удара, «пи-и-и 2с низкий» на крахе.
 *
 * Фазы store (PHASES) и сэмплера ecgKit совпадают по значениям ('betting'/'running'/'busted'),
 * поэтому сэмплер из ecgKit работает без адаптации. IDLE трактуем как betting (тишина).
 */
const CrashOscilloscope = () => {
  const canvasRef = useRef(null)
  const samplerRef = useRef(createSampler())
  const lastBeatPosRef = useRef(0)
  const bustedPlayedRef = useRef(false)
  const lastRoundIdRef = useRef(null)
  // Момент краха — для красной вспышки по полю
  const bustAtRef = useRef(null)

  useEffect(() => {
    let raf
    const frame = (now) => {
      raf = requestAnimationFrame(frame)
      const canvas = canvasRef.current
      if (!canvas) return

      const state = useCrashStore.getState()
      const { ctx, w, h } = fitCanvas(canvas)

      // Адаптируем состояние store под формат сэмплера
      const phase = state.phase
      const isRunning = phase === PHASES.RUNNING
      const isBusted = phase === PHASES.BUSTED
      const multiplierHundreds = state.multiplierHundreds || 100
      const roundId = state.roundId

      // Сброс сэмплера при новом раунде
      if (roundId !== lastRoundIdRef.current) {
        lastRoundIdRef.current = roundId
        bustedPlayedRef.current = false
        bustAtRef.current = null
      }

      // Сэмплер ожидает {id, phase, multiplierHundreds}
      // phase: IDLE трактуем как 'betting' (тишина до первого раунда)
      const samplerPhase = phase === PHASES.IDLE ? 'betting' : phase
      const samplerRound = {
        id: roundId || 'idle',
        phase: samplerPhase,
        multiplierHundreds
      }
      const s = samplerRef.current(samplerRound, now, { flatNoise: true })
      const alert = isBusted

      // Тик на R-пике (только в running)
      const BEAT_THRESHOLD = 0.03
      const prevBeatPos = lastBeatPosRef.current
      if (isRunning && prevBeatPos < BEAT_THRESHOLD && s.beatPos >= BEAT_THRESHOLD) {
        audioService.play('crashTickBeep')
      }
      lastBeatPosRef.current = s.beatPos

      // Звук краха — один раз при переходе в busted
      if (alert && !bustedPlayedRef.current) {
        bustedPlayedRef.current = true
        bustAtRef.current = now
        audioService.play('crashBustFlat2low')
      }

      // ─── Отрисовка ──────────────────────────────────────────────

      // Трубка: почти чёрная с зеленоватым отливом + лёгкое мерцание
      ctx.fillStyle = '#031007'
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = 0.93 + Math.random() * 0.07

      const midY = h * 0.58
      const amp = ampOf(s.logM, h)

      // Сетка осциллографа (слабая, по центру ярче)
      ctx.strokeStyle = 'rgba(0,255,127,0.05)'
      ctx.lineWidth = 1
      ctx.beginPath()
      for (let gx = 0; gx <= w; gx += 22) { ctx.moveTo(gx, 0); ctx.lineTo(gx, h) }
      for (let gy = 0; gy <= h; gy += 22) { ctx.moveTo(0, gy); ctx.lineTo(w, gy) }
      ctx.stroke()
      ctx.strokeStyle = 'rgba(0,255,127,0.12)'
      ctx.beginPath()
      ctx.moveTo(0, midY); ctx.lineTo(w, midY)
      ctx.moveTo(w / 2, 0); ctx.lineTo(w / 2, h)
      ctx.stroke()

      // Фосфорный след: segment-by-segment с затуханием по возрасту
      const lineColor = alert ? '255,75,75' : '0,255,127'
      for (let i = 1; i < s.samples.length; i++) {
        const a = s.samples[i - 1]
        const b = s.samples[i]
        const age = (now - b.t) / s.windowMs
        const alpha = Math.max(0, 1 - age) ** 1.6
        ctx.beginPath()
        ctx.moveTo(w - ((now - a.t) / s.windowMs) * w, midY - a.v * amp)
        ctx.lineTo(w - ((now - b.t) / s.windowMs) * w, midY - b.v * amp)
        ctx.strokeStyle = `rgba(${lineColor},${alpha})`
        ctx.lineWidth = 1.6 + alpha * 1.2
        ctx.stroke()
      }

      // Луч (яркая точка с ореолом) — только на живом сигнале
      const last = s.samples[s.samples.length - 1]
      if (last && !alert) {
        const bx = w - 3
        const by = midY - last.v * amp
        const beamR = 3.2
        const glow = ctx.createRadialGradient(bx, by, 0, bx, by, 22)
        glow.addColorStop(0, 'rgba(150,255,205,0.9)')
        glow.addColorStop(1, 'rgba(0,255,127,0)')
        ctx.fillStyle = glow
        ctx.beginPath()
        ctx.arc(bx, by, 22, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillStyle = '#EAFFF5'
        ctx.beginPath()
        ctx.arc(bx, by, beamR, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalAlpha = 1

      // Цифра-акцент по центру — герой экрана, подстраиваем под ширину поля.
      // В приём ставок вместо множителя — крупный таймер отсчёта + прогресс-полоса
      const digitSize = Math.min(76, Math.max(48, Math.round(w * 0.16)))
      ctx.textAlign = 'center'
      ctx.font = `700 ${digitSize}px "Consolas", "Courier New", monospace`
      ctx.fillStyle = alert ? ECG_COLORS.red : '#CFFFE7'
      ctx.shadowColor = alert ? ECG_COLORS.red : ECG_COLORS.line
      ctx.shadowBlur = 24

      if (phase === PHASES.BETTING && state.bettingDeadlineAt) {
        // Остаток окна ставок считаем по абсолютному дедлайну (как useCrashBettingTimer)
        const remaining = Math.max(0, state.bettingDeadlineAt - Date.now())
        ctx.fillText(`${Math.ceil(remaining / 1000)}s`, w / 2, digitSize + 48)
        ctx.shadowBlur = 0
        // Тонкая прогресс-полоса под таймером
        const progress = state.bettingMs > 0
          ? Math.max(0, Math.min(1, remaining / state.bettingMs))
          : 0
        const barW = Math.min(w * 0.5, 260)
        const barH = 4
        const barX = (w - barW) / 2
        const barY = digitSize + 48 + 12
        ctx.fillStyle = 'rgba(0,255,127,0.15)'
        ctx.fillRect(barX, barY, barW, barH)
        ctx.fillStyle = ECG_COLORS.line
        ctx.fillRect(barX, barY, barW * progress, barH)
      } else {
        const shown = phase === PHASES.IDLE
          ? '1.00'
          : hundredsToX(alert ? state.crashPoint : multiplierHundreds)
        ctx.fillText(`${shown}x`, w / 2, digitSize + 48)
        ctx.shadowBlur = 0
      }
      ctx.textAlign = 'left'

      // Красная вспышка по полю в момент краха (затухание 400мс)
      if (bustAtRef.current != null) {
        const flashK = (now - bustAtRef.current) / 400
        if (flashK < 1) {
          ctx.fillStyle = `rgba(255,75,75,${0.22 * (1 - flashK)})`
          ctx.fillRect(0, 0, w, h)
        }
      }
    }
    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <div className="relative w-full h-full overflow-hidden rounded-[24px]">
      <canvas ref={canvasRef} className="w-full h-full block" />
      {/* CRT-слои: scanline + виньетка + стекло */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'repeating-linear-gradient(to bottom, transparent 0 2px, rgba(0,0,0,0.22) 2px 3px)'
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.45) 92%, rgba(0,0,0,0.75) 100%)',
          boxShadow: 'inset 0 0 60px rgba(0,0,0,0.6)'
        }}
      />
    </div>
  )
}

export default CrashOscilloscope
